import React, { useCallback, ReactNode, useMemo, Fragment } from 'react';
import cx from 'clsx';

import { HelperTooltip } from '@/components';

import './index.scss';

type SegmentedProps<
  T extends {
    label: ReactNode;
    value: string;
    disabledTip?: ReactNode | boolean;
  },
> = {
  options?: T[];
  value?: string;
  onChange?(v: string, item: T): void;
  className?: string;
  color?: 'gray' | 'dialog' | 'form';
  hasSplit?: boolean;
  radius?: number;
  shadow?: boolean;
};

export const Segmented = <
  T extends {
    label: ReactNode;
    value: string;
    disabledTip?: ReactNode | boolean;
  },
>({
  options,
  value,
  onChange,
  className,
  color,
  hasSplit = true,
  radius = 0,
  shadow,
}: SegmentedProps<T>) => {
  const handleChange = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLDivElement>) => {
      const { value, idx } = currentTarget?.dataset || {};

      const item = options?.[Number(idx)];

      if (item && !item?.disabledTip) {
        onChange?.(value!, item);
      }
    },
    [onChange, options],
  );

  const radiusStyle = useMemo(() => {
    return radius
      ? {
          borderRadius: radius,
        }
      : undefined;
  }, [radius]);

  return options?.length ? (
    <div
      className={cx('db-segmented', `is-${color}`, shadow && 'is-shadow', className)}
      style={radiusStyle}>
      {options?.map((item, idx) => (
        <Fragment key={item.value}>
          <div
            data-value={item.value}
            data-idx={idx}
            className={cx(
              'db-segmentedItem',
              item.value === value && 'isActive',
              item.disabledTip && 'isDisabled',
            )}
            style={radiusStyle}
            onClick={handleChange}>
            <HelperTooltip trigger="hover" title={item.disabledTip} disable={!item.disabledTip}>
              <div> {item.label}</div>
            </HelperTooltip>
          </div>
          {idx === options.length - 1 || !hasSplit ? null : <div className="db-segmentedSplit" />}
        </Fragment>
      ))}
    </div>
  ) : null;
};
